import { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import colors from '../utils/style/colors'

const bgShift = keyframes`from{filter:brightness(1)}to{filter:brightness(1.1)saturate(1.1)}`
const fadeUp  = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`
const checkAnim = keyframes`0%{transform:scale(0)rotate(-20deg);opacity:0}60%{transform:scale(1.2)rotate(3deg)}100%{transform:scale(1)rotate(0);opacity:1}`

// ─── MINI HERO ───
const MiniHero = styled.section`
  position:relative;height:160px;display:flex;align-items:center;justify-content:center;
  overflow:hidden;background:#0e1a0a;
  @media(max-width:768px){height:130px}
`
const HeroBg = styled.div`
  position:absolute;inset:0;
  background:
    radial-gradient(ellipse 80% 60% at 20% 60%,rgba(58,90,46,.5) 0%,transparent 65%),
    radial-gradient(ellipse 70% 50% at 80% 40%,rgba(107,76,42,.35) 0%,transparent 60%),
    linear-gradient(160deg,#0e1a0a 0%,#1e2e14 40%,#2a1a0a 100%);
  animation:${bgShift} 10s ease-in-out infinite alternate;
`
const HeroGrain = styled.div`
  position:absolute;inset:0;pointer-events:none;opacity:.05;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size:200px
`
const HeroContent = styled.div`position:relative;z-index:2;text-align:center;animation:${fadeUp} .8s both`
const HeroBadge = styled.span`
  display:inline-block;background:rgba(200,168,75,.15);border:1px solid rgba(200,168,75,.4);
  color:#e8c96b;font-size:.7rem;font-weight:700;letter-spacing:.18em;text-transform:uppercase;
  padding:6px 16px;border-radius:2px;margin-bottom:12px;font-family:${colors.font};
`
const HeroTitle = styled.h1`
  font-family:'Playfair Display',Georgia,serif;font-size:clamp(1.6rem,4vw,2.6rem);
  font-weight:900;color:#fff;line-height:1.1;margin:0 0 8px;
  text-shadow:0 4px 24px rgba(0,0,0,.5);
  em{font-style:normal;color:#e8c96b}
`
const HeroSub = styled.p`
  font-family:${colors.font};font-size:.88rem;color:rgba(255,255,255,.6);font-weight:300;margin:0
`
const HeroBar = styled.div`
  position:absolute;bottom:0;left:0;right:0;height:4px;
  background:linear-gradient(90deg,transparent,#c8a84b,transparent)
`

// ─── PAGE ───
const PageWrap = styled.div`
  min-height:60vh;background:${colors.beige};
  display:flex;align-items:flex-start;justify-content:center;
  padding:60px 20px 80px;
  @media(max-width:768px){padding:32px 16px 60px}
`
const Card = styled.div`
  background:#faf8f4;border-radius:20px;
  box-shadow:0 20px 60px rgba(42,32,24,.12);
  width:100%;max-width:620px;overflow:hidden;
  animation:${fadeUp} .5s both;
`
const CardBody = styled.div`
  padding:40px 40px 44px;
  @media(max-width:500px){padding:28px 20px 36px}
`
const CardTitle = styled.h2`
  font-family:'Playfair Display',Georgia,serif;
  font-size:1.5rem;font-weight:700;color:${colors.primary};margin:0 0 6px;
`
const CardDesc = styled.p`
  font-family:${colors.font};font-size:.88rem;color:${colors.texteLeger};
  line-height:1.6;margin:0 0 32px;
`
const InfoRow = styled.div`
  display:flex;gap:24px;flex-wrap:wrap;margin-bottom:32px;
  padding:18px 20px;background:rgba(58,90,46,.05);
  border:1px solid rgba(58,90,46,.12);border-radius:10px;
`
const InfoItem = styled.div`
  display:flex;align-items:center;gap:8px;
  font-family:${colors.font};font-size:.8rem;color:${colors.texteLeger};
  span{font-size:1rem}
`

const FieldGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:14px;
  @media(max-width:540px){grid-template-columns:1fr}
`
const Field = styled.div`display:flex;flex-direction:column;gap:5px`
const Label = styled.label`
  font-size:.74rem;font-weight:600;letter-spacing:.08em;text-transform:uppercase;
  color:${colors.greentext};font-family:${colors.font};opacity:.85
`
const Input = styled.input`
  background:#fff;border:1.5px solid ${p=>p.$error?'#e05a5a':p.$valid?colors.vertOlive:'#e0d4c0'};
  border-radius:8px;padding:11px 14px;font-size:.9rem;font-family:${colors.font};
  color:${colors.primary};outline:none;width:100%;
  transition:border-color .2s,box-shadow .2s;
  &:focus{
    border-color:${p=>p.$error?'#e05a5a':colors.vertOlive};
    box-shadow:0 0 0 3px ${p=>p.$error?'rgba(224,90,90,.1)':'rgba(66,126,94,.1)'};
  }
`
const Textarea = styled.textarea`
  background:#fff;border:1.5px solid #e0d4c0;border-radius:8px;
  padding:11px 14px;font-size:.9rem;font-family:${colors.font};
  color:${colors.primary};outline:none;width:100%;resize:vertical;min-height:120px;
  transition:border-color .2s,box-shadow .2s;
  &:focus{border-color:${colors.vertOlive};box-shadow:0 0 0 3px rgba(66,126,94,.1)}
`
const ErrorMsg = styled.span`
  font-family:${colors.font};font-size:.72rem;color:#e05a5a;
  animation:${fadeUp} .2s ease both;
`
const BtnSubmit = styled.button`
  width:100%;margin-top:8px;
  background:${colors.or};color:${colors.texte};border:none;
  padding:14px 28px;border-radius:8px;font-family:${colors.font};
  font-size:.92rem;font-weight:700;letter-spacing:.06em;cursor:pointer;
  transition:all .25s;display:flex;align-items:center;justify-content:center;gap:8px;
  &:hover{background:${colors.orClair};transform:translateY(-1px);box-shadow:0 6px 20px rgba(200,168,75,.4)}
  &:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none}
`
const SuccessWrap = styled.div`
  padding:52px 40px;text-align:center;animation:${fadeUp} .5s ease both;
`
const SuccessIcon = styled.div`
  width:72px;height:72px;background:${colors.vertOlive};border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  font-size:1.9rem;margin:0 auto 20px;
  animation:${checkAnim} .5s .1s ease both;
`
const SuccessTitle = styled.h2`
  font-family:'Playfair Display',Georgia,serif;font-size:1.5rem;
  font-weight:700;color:${colors.primary};margin-bottom:10px;
`
const SuccessSub = styled.p`
  color:${colors.texteLeger};font-size:.92rem;font-family:${colors.font};
  line-height:1.65;max-width:380px;margin:0 auto;
`

// ─── VALIDATION ───
const validateEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

const Hero = (
  <MiniHero>
    <HeroBg/><HeroGrain/>
    <HeroContent>
      <HeroBadge>✉️ Contact</HeroBadge>
      <HeroTitle>Contactez-<em>nous</em></HeroTitle>
      <HeroSub>Réponse garantie sous 24h ouvrées</HeroSub>
    </HeroContent>
    <HeroBar/>
  </MiniHero>
)

export default function FormContact() {
  const [form, setForm] = useState({ Nom:'', Email:'', Téléphone:'', Message:'' })
  const [touched, setTouched] = useState({})
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)

  const set = (f,v) => setForm(p=>({...p,[f]:v}))
  const touch = f => setTouched(p=>({...p,[f]:true}))

  const errors = {
    Nom:     form.Nom.trim().length < 2 ? 'Merci d\'indiquer votre nom' : null,
    Email:   !validateEmail(form.Email) ? 'Adresse email invalide' : null,
    Message: form.Message.trim().length < 10 ? 'Message trop court' : null,
  }
  const isValid = !errors.Nom && !errors.Email && !errors.Message

  const handleSubmit = async () => {
    setTouched({ Nom:true, Email:true, Message:true })
    if (!isValid) return
    setLoading(true)
    const fd = new FormData()
    fd.append('Nom', form.Nom)
    fd.append('Email', form.Email)
    if (form.Téléphone) fd.append('Téléphone', form.Téléphone)
    fd.append('Message', form.Message)
    try {
      await fetch('https://formspree.io/f/xblyrzvk', {
        method:'POST', body:fd, headers:{ Accept:'application/json' }
      })
    } catch {}
    setLoading(false)
    setSent(true)
  }

  if (sent) return (
    <>{Hero}
    <PageWrap><Card>
      <SuccessWrap>
        <SuccessIcon>✓</SuccessIcon>
        <SuccessTitle>Message envoyé !</SuccessTitle>
        <SuccessSub>
          Merci {form.Nom.split(' ')[0]} ! Votre message a bien été reçu.
          Nous vous répondons sous 24h ouvrées.
        </SuccessSub>
      </SuccessWrap>
    </Card></PageWrap></>
  )

  return (
    <>{Hero}
    <PageWrap><Card>
      <CardBody>
        <CardTitle>Une question ?</CardTitle>
        <CardDesc>Contactez-nous pour toute question sur un produit, une commande ou une demande spéciale.</CardDesc>

        <InfoRow>
          <InfoItem><span>⏱</span> Réponse sous 24h</InfoItem>
          <InfoItem><span>🚛</span> Livraison France → Suisse</InfoItem>
          <InfoItem><span>🇫🇷</span> Origine France garantie</InfoItem>
        </InfoRow>

        <FieldGrid>
          <Field>
            <Label>Nom / Prénom *</Label>
            <Input
              value={form.Nom} placeholder="Jean-Pierre Müller"
              onChange={e=>set('Nom',e.target.value)}
              onBlur={()=>touch('Nom')}
              $error={touched.Nom&&!!errors.Nom}
              $valid={!errors.Nom&&form.Nom}
            />
            {touched.Nom&&errors.Nom&&<ErrorMsg>⚠ {errors.Nom}</ErrorMsg>}
          </Field>
          <Field>
            <Label>Email *</Label>
            <Input
              type="email" value={form.Email} placeholder="votre@email.ch"
              onChange={e=>set('Email',e.target.value)}
              onBlur={()=>touch('Email')}
              $error={touched.Email&&!!errors.Email}
              $valid={!errors.Email&&form.Email}
            />
            {touched.Email&&errors.Email&&<ErrorMsg>⚠ {errors.Email}</ErrorMsg>}
          </Field>
        </FieldGrid>

        <Field style={{marginTop:14}}>
          <Label>Téléphone</Label>
          <Input
            type="tel" value={form.Téléphone} placeholder="+41 79 000 00 00"
            onChange={e=>set('Téléphone',e.target.value)}
          />
        </Field>

        <Field style={{marginTop:14}}>
          <Label>Message *</Label>
          <Textarea
            value={form.Message}
            placeholder="Votre question ou message…"
            onChange={e=>set('Message',e.target.value)}
            onBlur={()=>touch('Message')}
          />
          {touched.Message&&errors.Message&&<ErrorMsg>⚠ {errors.Message}</ErrorMsg>}
        </Field>

        <BtnSubmit onClick={handleSubmit} disabled={loading}>
          {loading ? 'Envoi…' : '✉️  Envoyer le message'}
        </BtnSubmit>
      </CardBody>
    </Card></PageWrap></>
  )
}
